import * as Sentry from '@sentry/react';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { InventoryProvider } from './contexts/InventoryContext';
import { DataCacheProvider } from './contexts/DataCacheContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { NetworkProvider } from './contexts/NetworkContext';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { GuestRoute } from './components/auth/GuestRoute';
import { PageGuard } from './components/auth/PageGuard';

// Pages
import { WelcomePage } from './pages/WelcomePage';
import { LoginPage } from './pages/LoginPage';
import { ActivationPage } from './pages/ActivationPage';
import ActivationRequiredPage from './pages/ActivationRequiredPage';
import { Dashboard } from './pages/Dashboard';
import { Inventory } from './pages/Inventory';
import { IncomingShipments } from './pages/IncomingShipments';
import { OutgoingShipments } from './pages/OutgoingShipments';
import { PackageTracing } from './pages/PackageTracing';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { SuperAdminPanel } from './pages/SuperAdminPanel';
import { ConnectionTest } from './pages/ConnectionTest';
import { DeviceManager } from './pages/DeviceManager';
import { BarcodeGenerator } from './pages/BarcodeGenerator';
import { InHouseShipment } from './pages/InHouseShipment';
import { ReturnShipment } from './pages/ReturnShipment';
import { ShippingLabels } from './pages/ShippingLabels';
import { SalesInvoices } from './pages/SalesInvoices';
import { UserManagement } from './pages/UserManagement';
import { InvoiceViewer } from './pages/InvoiceViewer';
import { ShopifyIntegration } from './pages/ShopifyIntegration';
import { ShopifySyncSettings } from './pages/ShopifySyncSettings';
import { ShopifyOrders } from './pages/ShopifyOrders';
import { WarehouseDetailPage } from './pages/WarehouseDetailPage';
import { WorkerTerminal } from './pages/WorkerTerminal';
import { ReservationTerminal } from './pages/ReservationTerminal';
import { ShopTerminal } from './pages/ShopTerminal';
import { ShopStockCount } from './pages/ShopStockCount';
import { AnalyticsDashboard } from './pages/AnalyticsDashboard';
import { SalesHistory } from './pages/SalesHistory';
import { OrdersIssue } from './pages/OrdersIssue';
import { KeyboardShortcutsHelp } from './components/ui/KeyboardShortcutsHelp';
import { B2BPage } from './pages/B2BPage';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NetworkProvider>
          <ThemeProvider>
            <LanguageProvider>
              <NotificationProvider>
                <DataCacheProvider>
                  <InventoryProvider>
                  <KeyboardShortcutsHelp />
                  <SentryRoutes>
                  {/* Public Routes */}
                  <Route path="/" element={<GuestRoute><WelcomePage /></GuestRoute>} />
                  <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
                  <Route path="/activation" element={<ActivationPage />} />
                  <Route path="/activation-required" element={<ActivationRequiredPage />} />
                  <Route path="/connection-test" element={<ConnectionTest />} />

                  {/* Invoice Viewer - Public for printing */}
                  <Route path="/invoice/:id" element={
                    <ProtectedRoute>
                      <InvoiceViewer />
                    </ProtectedRoute>
                  } />

                  {/* Protected Routes - Regular Users */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/inventory" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="inventory">
                          <Inventory />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/incoming" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="incoming">
                          <IncomingShipments />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/outgoing" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="outgoing">
                          <OutgoingShipments />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/package-tracing" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="package-tracing">
                          <PackageTracing />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/reports" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="reports">
                          <Reports />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Layout>
                        <Settings />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/devices" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="devices">
                          <DeviceManager />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/barcode-generator" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="barcode-generator">
                          <BarcodeGenerator />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/in-house-shipment" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="in-house-shipment">
                          <InHouseShipment />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/return-shipment" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="return-shipment">
                          <ReturnShipment />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/shipping-labels" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="shipping-labels">
                          <ShippingLabels />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/orders-issues" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="orders-issues">
                          <OrdersIssue />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/sales-invoices" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="sales-invoices">
                          <SalesInvoices />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/shopify" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="shopify">
                          <ShopifyIntegration />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/settings/shopify-sync" element={
                    <ProtectedRoute>
                      <Layout>
                        <ShopifySyncSettings />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/shopify/orders" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="shopify-orders">
                          <ShopifyOrders />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/warehouse/:id" element={
                    <ProtectedRoute>
                      <Layout>
                        <WarehouseDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/worker-terminal" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="worker-terminal">
                          <WorkerTerminal />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/reservation-terminal" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="reservation-terminal">
                          <ReservationTerminal />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/shop-terminal" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="shop-terminal">
                          <ShopTerminal />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/shop-stock-count" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="shop-stock-count">
                          <ShopStockCount />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/analytics" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="analytics">
                          <AnalyticsDashboard />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/sales-history" element={
                    <ProtectedRoute>
                      <Layout>
                        <PageGuard pageKey="sales-history">
                          <SalesHistory />
                        </PageGuard>
                      </Layout>
                    </ProtectedRoute>
                  } />

                  {/* B2B Portal - accessible by buyers and admins */}
                  <Route path="/b2b" element={
                    <ProtectedRoute requireBuyer={true} requirePayment={false}>
                      <B2BPage />
                    </ProtectedRoute>
                  } />

                  {/* Admin Routes */}
                  <Route path="/admin/users" element={
                    <ProtectedRoute>
                      <Layout>
                        <UserManagement />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  {/* SuperAdmin Routes */}
                  <Route path="/admin" element={
                    <ProtectedRoute requireSuperAdmin={true}>
                      <Layout>
                        <SuperAdminPanel />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                  </SentryRoutes>
                  </InventoryProvider>
                </DataCacheProvider>
              </NotificationProvider>
            </LanguageProvider>
          </ThemeProvider>
        </NetworkProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;